/* ==========================================================================
   Reviews slider — turns the review cards into a swipeable slideshow with
   clickable pagination dots and optional autoplay. Progressive enhancement on
   top of the native scroll-snap track (works without JS too).
   ========================================================================== */
if (!customElements.get('reviews-slider')) {
  customElements.define(
    'reviews-slider',
    class ReviewsSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.cards = Array.from(this.track.children);
        this.dotsWrap = this.querySelector('[data-dots]');
        this.dots = Array.from(this.querySelectorAll('.plate-reviews__dot'));

        if (this.cards.length < 2) {
          if (this.dotsWrap) this.dotsWrap.hidden = true;
          return;
        }

        this.autoplayMs = parseInt(this.dataset.autoplay, 10) || 0;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.active = 0;

        this.dots.forEach((dot, i) =>
          dot.addEventListener('click', () => {
            this.goto(i);
            this.restart();
          })
        );

        this.onScroll = this.rafThrottle(() => this.syncActive());
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.onResize = this.rafThrottle(() => this.updateOverflow());
        window.addEventListener('resize', this.onResize);

        this.addEventListener('pointerenter', () => this.stop());
        this.addEventListener('pointerleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());

        this.updateOverflow();
        this.syncActive();
        this.start();
      }

      disconnectedCallback() {
        this.stop();
        window.removeEventListener('resize', this.onResize);
      }

      updateOverflow() {
        this.hasOverflow = this.track.scrollWidth > this.track.clientWidth + 4;
        if (this.dotsWrap) this.dotsWrap.hidden = !this.hasOverflow;
        if (!this.hasOverflow) this.stop();
        else this.start();
      }

      indexAtStart() {
        const trackLeft = this.track.getBoundingClientRect().left;
        let best = 0;
        let bestDist = Infinity;
        this.cards.forEach((card, i) => {
          const dist = Math.abs(card.getBoundingClientRect().left - trackLeft);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        });
        return best;
      }

      syncActive() {
        this.active = this.indexAtStart();
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.active));
      }

      goto(i) {
        const card = this.cards[i];
        if (!card) return;
        const delta = card.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
        this.track.scrollBy({ left: delta, behavior: this.reduceMotion ? 'auto' : 'smooth' });
      }

      next() {
        const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
        this.goto(atEnd ? 0 : Math.min(this.active + 1, this.cards.length - 1));
      }

      start() {
        if (this.timer || !this.autoplayMs || this.reduceMotion || !this.hasOverflow) return;
        this.timer = setInterval(() => this.next(), this.autoplayMs);
      }

      stop() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }

      restart() {
        this.stop();
        this.start();
      }

      rafThrottle(fn) {
        let ticking = false;
        return () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            fn();
          });
        };
      }
    }
  );
}
